import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="card-search"
export default class extends Controller {
  static targets = ["input", "results", "cardId", "selected", "cardName", "cardSet", "cardPrice"]
  static values = { url: String }

  connect() {
    this.timeout = null
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.boundHandleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleClickOutside)
    if (this.timeout) clearTimeout(this.timeout)
  }

  search() {
    if (this.timeout) clearTimeout(this.timeout)

    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const cards = await response.json()
      this.displayResults(cards)
    } catch (error) {
      console.error("Card search error:", error)
      this.hideResults()
    }
  }

  displayResults(cards) {
    if (cards.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="p-4 text-sm text-slate-500">No cards found</div>
      `
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const html = cards.map(card => `
      <button type="button"
              class="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              data-action="click->card-search#select"
              data-card-id="${card.id}"
              data-card-name="${this.escapeHtml(card.name)}"
              data-card-set="${this.escapeHtml(card.card_set || '')}"
              data-card-price="${card.loose_price || ''}">
        <div class="font-semibold text-slate-900">${this.escapeHtml(card.name)}</div>
        <div class="text-sm text-slate-600">${this.escapeHtml(card.card_set || '')}</div>
        ${card.loose_price ? `<div class="text-sm text-emerald-600">$${card.loose_price.toFixed(2)}</div>` : ''}
      </button>
    `).join("")

    this.resultsTarget.innerHTML = html
    this.resultsTarget.classList.remove("hidden")
  }

  select(event) {
    event.preventDefault()
    const button = event.currentTarget

    const cardId = button.dataset.cardId
    const cardName = button.dataset.cardName
    const cardSet = button.dataset.cardSet
    const cardPrice = button.dataset.cardPrice

    // Update hidden field
    this.cardIdTarget.value = cardId

    // Update display
    this.cardNameTarget.textContent = cardName
    this.cardSetTarget.textContent = cardSet
    this.cardPriceTarget.textContent = cardPrice ? `Market: $${parseFloat(cardPrice).toFixed(2)}` : ""

    // Show selected, hide results
    this.selectedTarget.classList.remove("hidden")
    this.hideResults()
    this.inputTarget.value = ""

    // Trigger price preview update
    this.dispatch("selected", { detail: { cardId, cardName, cardSet, cardPrice } })
  }

  clear(event) {
    event.preventDefault()

    // Clear hidden field
    this.cardIdTarget.value = ""

    // Hide selected display
    this.selectedTarget.classList.add("hidden")

    // Clear input
    this.inputTarget.value = ""

    // Trigger price preview clear
    this.dispatch("cleared")
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
